import { colorForKey } from '@utils/objectColor';
import { getBaseUnit, getConversionFactor } from '@utils/measurementSystem';

/**
 * Transformation utilities for collection detection data.
 *
 * These pure functions handle the transformation from raw API responses
 * to UI-ready data structures, including the extended range logic for filters.
 */

/** Maximum decimal places for filter range bounds displayed in the UI. */
const DISPLAY_PRECISION = 1;

/** Floors a value to DISPLAY_PRECISION (rounds min bounds outward). */
const floorDisplay = v => {
	if (v == null) return v;
	const factor = Math.pow(10, DISPLAY_PRECISION);
	return Math.floor(v * factor) / factor;
};

/** Ceils a value to DISPLAY_PRECISION (rounds max bounds outward). */
const ceilDisplay = v => {
	if (v == null) return v;
	const factor = Math.pow(10, DISPLAY_PRECISION);
	return Math.ceil(v * factor) / factor;
};

/**
 * Prettify a parameter id for display.
 * Converts snake_case or kebab-case to Title Case. Not for object names: those
 * come from the collection (see `objectLabel`).
 *
 * @param {string} key - The parameter id (e.g., "confidence", "mean_height")
 * @returns {string} Prettified label (e.g., "Confidence", "Mean Height")
 */
export const prettify = key =>
	String(key ?? '')
		.replace(/[_-]+/g, ' ')
		.replace(/\b\w/g, char => char.toUpperCase())
		.trim();

/** `hsl(h, s%, l%)` -> `#rrggbb`. Expects already-parsed numbers. */
const hslToHex = (h, s, l) => {
	const sat = s / 100;
	const light = l / 100;
	const a = sat * Math.min(light, 1 - light);
	const channel = n => {
		const k = (n + h / 30) % 12;
		const value = light - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
		return Math.round(value * 255)
			.toString(16)
			.padStart(2, '0');
	};
	return `#${channel(0)}${channel(8)}${channel(4)}`;
};

const HEX_PATTERN = /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const HSL_PATTERN = /^hsla?\(\s*([\d.]+)\s*,\s*([\d.]+)%\s*,\s*([\d.]+)%/i;

/**
 * Normalizes a stored color to hex, the one form the rest of the collection UI
 * accepts.
 *
 * Colors reach `style.color` from two places and only one of them is hex: the
 * object panel writes hex, while collections created by the old Object Picker
 * flow persisted whatever `colorForKey` produced for a taxonomy node the backend
 * gave no color, and that
 * is an `hsl(...)` string. Left alone those objects render no swatch in the tree
 * (`CollectionNode` accepts hex only) and reach the map through `hexToRgba`,
 * which slices the string positionally and yields `rgba(NaN, NaN, NaN, …)` at any
 * opacity below 100.
 *
 * Short hex is expanded so consumers can slice by position. An unrecognized
 * format returns undefined rather than passing through: callers already fall back
 * to a default color, which beats a swatch and a map disagreeing.
 *
 * @param {string} [color]
 * @returns {string|undefined} `#rrggbb` or `#rrggbbaa`
 */
export const toHexColor = color => {
	if (!color || typeof color !== 'string') return undefined;
	const trimmed = color.trim();

	if (HEX_PATTERN.test(trimmed)) {
		if (trimmed.length > 5) return trimmed;
		// #rgb / #rgba -> #rrggbb / #rrggbbaa
		return `#${trimmed
			.slice(1)
			.split('')
			.map(char => char + char)
			.join('')}`;
	}

	const hsl = HSL_PATTERN.exec(trimmed);
	if (hsl) return hslToHex(Number(hsl[1]), Number(hsl[2]), Number(hsl[3]));

	return undefined;
};

/**
 * Map a collection's raw `detection_objects` (from the config endpoint) to the
 * `{ key, label, color }` shape the shared detection summary renders.
 *
 * Deliberately built from the config rather than from `data.objects`: the latter
 * comes from the detection results and so only lists objects that already have
 * detections. When asking the user to confirm a *re-run*, the objects with zero
 * detections matter most — they're what a new run might find.
 *
 * Entries without a node uuid are dropped (nothing useful to show or match on),
 * and a missing `style.color` falls back to the same uuid-derived color the
 * Object Picker uses, so the same object looks the same in both places.
 *
 * @param {Array<{node_uuid?: string, name?: string, style?: {color?: string}}>} detectionObjects
 * @returns {Array<{key: string, label: string, color: string}>}
 */
export const toSummaryObjects = (detectionObjects = []) =>
	(detectionObjects ?? [])
		.filter(object => object?.node_uuid)
		.map(object => ({
			key: object.node_uuid,
			label: object.name || object.node_uuid,
			color: object.style?.color ?? colorForKey(object.node_uuid),
		}));

/**
 * An object's display name, from its node uuid. Detections, hexagon cells and
 * sampling plots name objects only by uuid; the collection's objects carry the
 * name. Falls back to the uuid itself rather than inventing a label.
 *
 * @param {Array<{key: string, label?: string}>} objects - the collection's objects
 * @param {string} nodeUuid
 * @returns {string}
 */
export const objectLabel = (objects, nodeUuid) =>
	(objects ?? []).find(object => object.key === nodeUuid)?.label || nodeUuid;

/**
 * Get the appropriate display unit for a parameter, respecting the user's
 * measurement system preference (metric/imperial).
 *
 * Called at render time (not cached in data transforms) so it always reflects
 * the current measurement system setting.
 *
 * @param {string} parameterId - The parameter ID (e.g., "confidence", "area", "height")
 * @returns {string} The unit symbol or empty string
 */
export const getUnit = parameterId => {
	switch (parameterId) {
		case 'confidence':
			return '%';
		case 'area':
		case 'bbox_area':
			return getBaseUnit('area');
		case 'radius':
		case 'height':
			return getBaseUnit('length');
		default:
			return '';
	}
};

/**
 * Get the conversion factor for a parameter's measurement type.
 * Returns 1 for unitless parameters or unknown types.
 *
 * Confidence is stored as 0–1 in the API but displayed as 0–100% in the UI.
 *
 * @param {string} parameterId
 * @returns {number}
 */
const getParameterConversionFactor = parameterId => {
	switch (parameterId) {
		case 'confidence':
			return 100;
		case 'area':
		case 'bbox_area':
			return getConversionFactor('area');
		case 'radius':
		case 'height':
			return getConversionFactor('length');
		default:
			return 1;
	}
};

/**
 * Parameters excluded from the filter UI.
 * bbox_area is redundant — the Area filter already covers detection area.
 */
const EXCLUDED_PARAMETERS = new Set(['bbox_area']);

/**
 * Build filter configs with extended range logic.
 *
 * Collections are reusable across projects with different detection results.
 * If saved filter values fall outside the current project's detection range,
 * we extend the available range rather than clamping saved values.
 *
 * Extended Range Logic:
 *   availableMin = min(detectionParams.min, savedFilter.min)
 *   availableMax = max(detectionParams.max, savedFilter.max)
 *
 * Example:
 *   Detection range: { min: 0.40, max: 0.75 }
 *   Saved selection: { min: 0.6, max: 0.8 }
 *   → Available range: { min: 0.40, max: 0.8 } (extended to include saved max)
 *   → Selected: { min: 0.6, max: 0.8 } (unchanged)
 *
 * This preserves saved filter values exactly without clamping, allowing users
 * to see their previous selections even when switching between projects.
 *
 * @param {object} parameters - Detection parameters from API (e.g., { confidence: { min, max } })
 * @param {object} savedFilters - Saved filter selections from collection config
 * @returns {Array<object>} Array of filter configs ready for UI display
 */
export const buildExtendedFilters = (parameters = {}, savedFilters = {}) => {
	return Object.entries(parameters)
		.filter(([parameterId]) => !EXCLUDED_PARAMETERS.has(parameterId))
		.map(([parameterId, detectionParams]) => {
			const savedFilter = savedFilters[parameterId];

			// Extend available range to accommodate saved values
			const availableMin =
				savedFilter?.min != null
					? Math.min(detectionParams.min, savedFilter.min)
					: detectionParams.min;

			const availableMax =
				savedFilter?.max != null
					? Math.max(detectionParams.max, savedFilter.max)
					: detectionParams.max;

			return {
				id: parameterId,
				label: prettify(parameterId),
				unit: getUnit(parameterId),
				conversionFactor: getParameterConversionFactor(parameterId),
				min: floorDisplay(availableMin),
				max: ceilDisplay(availableMax),
				selectedMin: savedFilter?.min != null ? savedFilter.min : null,
				selectedMax: savedFilter?.max != null ? savedFilter.max : null,
			};
		});
};

/**
 * Tests whether a single feature's property value passes a filter range.
 *
 * Null handling is *per-predicate*: a null/absent value cannot satisfy an active
 * bound, so it is excluded when this parameter is filtered (min and/or max set),
 * and passes untouched when it is not (both null = no restriction). This matches
 * the collection hexagons endpoint, whose SQL drops NULL-valued detections for a
 * filtered parameter — keeping the client-side detection list in agreement with
 * the server-aggregated hexagon counts. Because the exclusion is scoped to the
 * parameter being filtered, a null on an *unfiltered* parameter never removes a
 * detection.
 *
 * @param {number|null|undefined} value
 * @param {number|null} min - null means "no lower limit"
 * @param {number|null} max - null means "no upper limit"
 * @returns {boolean}
 */
const passesRange = (value, min, max) => {
	if (value == null) {
		// Excluded only when this predicate is active; otherwise not filtered out.
		return min == null && max == null;
	}
	if (min != null && value < min) return false;
	if (max != null && value > max) return false;
	return true;
};

/** Active entries of a { [filterId]: { min, max } } map — null/null is no limit. */
const toActiveFilters = valueMap =>
	Object.entries(valueMap ?? {})
		.filter(([, v]) => v?.min != null || v?.max != null)
		.map(([id, v]) => ({ id, min: v.min, max: v.max }));

/** The same, per node_uuid, dropping objects with nothing active. */
const toActiveObjectFilters = objectFilterValues => {
	const byKey = {};
	for (const [objectKey, filterMap] of Object.entries(objectFilterValues ?? {})) {
		const active = toActiveFilters(filterMap);
		if (active.length) byKey[objectKey] = active;
	}
	return byKey;
};

/**
 * Whether one feature clears the collection-level filters and its own object's.
 * Shared by the map pass and the counting pass so the two can't disagree.
 */
const passesFilters = (props, activeCollectionFilters, activeObjectFilters) => {
	for (const f of activeCollectionFilters) {
		if (!passesRange(props[f.id], f.min, f.max)) return false;
	}

	const objFilters = activeObjectFilters[props.node_uuid];
	if (objFilters) {
		for (const f of objFilters) {
			if (!passesRange(props[f.id], f.min, f.max)) return false;
		}
	}

	return true;
};

/**
 * Filters a GeoJSON feature collection client-side using the current filter state.
 *
 * Filter levels applied in order:
 *   1. Visibility — feature's node_uuid must be in checkedKeys.
 *   2. Collection-level filters — applied to all features regardless of node_uuid.
 *   3. Per-object filters — applied only to features matching that node_uuid.
 *
 * Filter values are passed as plain { [filterId]: { min, max } } maps — the live
 * form draft values — rather than config arrays with selectedMin/Max baked in.
 * This avoids merging/transforming configs at call time and keeps the interface
 * minimal: only the values needed for the comparison are passed.
 *
 * A filter entry is skipped (treated as full-range) when its value is absent,
 * or when both min and max are null (meaning "no restriction").
 *
 * @param {Array}  features              - GeoJSON feature array from the API
 * @param {object} checkedKeys           - { [objectKey]: true } — visible objects
 * @param {object} collectionFilterValues - { [filterId]: { min, max } } — collection-level live values
 * @param {object} objectFilterValues     - { [objectKey]: { [filterId]: { min, max } } } — per-object live values
 * @returns {Array} Filtered GeoJSON feature array (same object references, no cloning)
 */
export const filterFeatures = (
	features,
	checkedKeys,
	collectionFilterValues,
	objectFilterValues
) => {
	if (!features?.length) return features ?? [];

	const activeCollectionFilters = toActiveFilters(collectionFilterValues);
	const activeObjectFilters = toActiveObjectFilters(objectFilterValues);

	return features.filter(feature => {
		const props = feature.properties ?? {};
		// Visibility first: a hidden object costs one lookup, not a filter sweep.
		if (!checkedKeys[props.node_uuid]) return false;
		return passesFilters(props, activeCollectionFilters, activeObjectFilters);
	});
};

/**
 * How many detections pass the filters, per node_uuid — ignoring visibility.
 *
 * The tree shows this rather than `filterFeatures`' tally so that every row
 * reports the same thing. Unticking an object only removes it from the map; it
 * doesn't change how many of its detections clear the filters, and a row that
 * switched metrics on a checkbox read as the filter having been undone.
 *
 * @param {Array}  features
 * @param {object} collectionFilterValues - { [filterId]: { min, max } }
 * @param {object} objectFilterValues     - { [objectKey]: { [filterId]: { min, max } } }
 * @returns {object} - { [objectKey]: count }
 */
export const countPassingByKey = (features, collectionFilterValues, objectFilterValues) => {
	const counts = {};
	if (!features?.length) return counts;

	const activeCollectionFilters = toActiveFilters(collectionFilterValues);
	const activeObjectFilters = toActiveObjectFilters(objectFilterValues);

	for (const feature of features) {
		const props = feature.properties ?? {};
		const objectKey = props.node_uuid;
		if (objectKey == null) continue;
		if (!passesFilters(props, activeCollectionFilters, activeObjectFilters)) continue;
		counts[objectKey] = (counts[objectKey] ?? 0) + 1;
	}

	return counts;
};

/**
 * Groups numeric feature-property values by property key, for use as the raw
 * input to a filter's distribution chart (see PropertyHistogram).
 *
 * Optionally scoped to a single object's detections via `objectKey` — the
 * per-object filter panel only wants that object's values, while the
 * collection-level panel wants every feature's values.
 *
 * @param {Array} features - GeoJSON feature array (e.g. data.features from useCollectionData)
 * @param {string|null} [objectKey] - When set, only features with a matching
 *   `properties.node_uuid` are included; when null/undefined, all features are.
 * @returns {object} { [propertyId]: Array<number> }
 */
export const buildHistogramValues = (features, objectKey = null) => {
	const values = {};
	for (const feature of features ?? []) {
		const props = feature.properties ?? {};
		if (objectKey != null && props.node_uuid !== objectKey) continue;

		for (const [key, value] of Object.entries(props)) {
			if (typeof value !== 'number') continue;
			(values[key] ??= []).push(value);
		}
	}
	return values;
};

/**
 * Build collection-level filters by aggregating across all objects.
 *
 * Takes the union of parameter ranges across all detection objects and
 * applies the same extended range logic with saved collection-level filters.
 *
 * @param {Array<object>} detectionObjects - Detection metadata objects from API
 * @param {object} savedCollectionFilters - Saved collection-level filters
 * @returns {Array<object>} Array of filter configs for collection-level filtering
 */
export const buildCollectionFilters = (detectionObjects = [], savedCollectionFilters = {}) => {
	// Aggregate parameter ranges across all objects
	const aggregatedParams = {};

	for (const obj of detectionObjects) {
		if (!obj.parameters) continue;

		for (const [parameterId, range] of Object.entries(obj.parameters)) {
			if (!aggregatedParams[parameterId]) {
				aggregatedParams[parameterId] = {
					min: range.min,
					max: range.max,
				};
			} else {
				aggregatedParams[parameterId].min = Math.min(
					aggregatedParams[parameterId].min,
					range.min
				);
				aggregatedParams[parameterId].max = Math.max(
					aggregatedParams[parameterId].max,
					range.max
				);
			}
		}
	}

	// Apply extended range logic
	return buildExtendedFilters(aggregatedParams, savedCollectionFilters);
};
